(function(){'use strict';
// Strict store inherited from p2-local; local UI extension adds reviewed atomic import.
// No browser globals or network operations in this module.
const STORAGE_KEY = 'cs_inspiration_v1';
const TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ITEMS = 8;
const MAX_RECORD_BYTES = 16384;
const MAX_LEGACY_BYTES = 65536;
const itemFields = ['type', 'key', 'line', 'source', 'content_version'];
const envelopeFields = ['schema_version', 'catalog_namespace', 'registry_version', 'created_at', 'expires_at', 'items'];
const own = (object, fields) => object !== null && typeof object === 'object' &&
  [Object.prototype, null].includes(Object.getPrototypeOf(object)) &&
  Reflect.ownKeys(object).length === fields.length && fields.every(key =>
    Object.hasOwn(object, key) && Object.hasOwn(Object.getOwnPropertyDescriptor(object, key), 'value'));
const token = value => typeof value === 'string' && /^[a-zA-Z0-9:_-]{1,80}$/.test(value);
const identity = value => own(value, ['type', 'key']) && ['product', 'look', 'direction'].includes(value.type) &&
  typeof value.key === 'string' && (value.type === 'product' ? /^[1-9][0-9]{0,19}$/ : /^[a-z0-9][a-z0-9:-]{0,79}$/).test(value.key);
const id = value => JSON.stringify([value.type, value.key]);
const copy = value => JSON.parse(JSON.stringify(value));
const freeze = value => {
  if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); }
  return value;
};
const bytes = value => new TextEncoder().encode(value).length;
const timeOK = value => Number.isSafeInteger(value) && value >= 0;
const reference = item => Object.fromEntries(itemFields.map(field => [field, item[field]]));
const toIdentity = item => ({ type: item.type, key: item.key });

// Existing Shopify product handles can contain Romanian characters. Accept
// only their exact canonical UTF-8 encoding, never encoded URL delimiters.
function registryResource(value) {
  if (typeof value !== 'string' || value.length > 500) return false;
  if (/^(?:\/(?!\/)[a-zA-Z0-9/_-]+|shopify:\/\/shop_images\/[a-zA-Z0-9_.-]+)$/.test(value)) return true;
  const match = /^\/products\/([^/?#]+)$/.exec(value);
  if (!match) return false;
  try {
    const handle = decodeURIComponent(match[1]);
    return /^[\p{L}\p{N}-]{1,200}$/u.test(handle) && encodeURIComponent(handle) === match[1];
  } catch { return false; }
}

/** Registry is supplied by trusted build code, never by storage or URL payloads. */
function createRegistry(definition) {
  if (!own(definition, ['catalog_namespace', 'version', 'entries']) || !token(definition.catalog_namespace) ||
      !token(definition.version) || !Array.isArray(definition.entries) || definition.entries.length > 2000) {
    throw new TypeError('Invalid registry envelope');
  }
  const entries = new Map();
  const legacy = new Map();
  for (const value of definition.entries) {
    const fields = [...itemFields, 'state', 'selectable', 'labels', 'resource'];
    if (Object.hasOwn(value, 'legacy')) fields.push('legacy');
    if (!own(value, fields) || !identity(toIdentity(value)) || !['men', 'donna'].includes(value.line) ||
        !token(value.source) || !token(value.content_version) || !['available', 'unavailable'].includes(value.state) ||
        typeof value.selectable !== 'boolean' || !own(value.labels, ['ro', 'en']) ||
        !Object.values(value.labels).every(label => typeof label === 'string' && label.length > 0 && label.length <= 240 && !/[<>\u0000-\u001f]/.test(label)) ||
        !registryResource(value.resource) || entries.has(id(value))) {
      throw new TypeError('Invalid or duplicate registry item');
    }
    const entry = freeze(copy(value));
    entries.set(id(entry), entry);
    if (entry.legacy) {
      if (!own(entry.legacy, ['kind', 'id']) || !['prefall', 'bomber'].includes(entry.legacy.kind) ||
          !token(entry.legacy.id) || (entry.legacy.kind === 'prefall' ? entry.type !== 'look' : entry.type !== 'direction')) {
        throw new TypeError('Invalid legacy mapping');
      }
      const key = JSON.stringify([entry.legacy.kind, entry.legacy.id]);
      if (legacy.has(key)) throw new TypeError('Duplicate legacy mapping');
      legacy.set(key, entry);
    }
  }
  return Object.freeze({
    catalog_namespace: definition.catalog_namespace,
    version: definition.version,
    resolve: value => identity(value) ? entries.get(id(value)) || null : null,
    resolveLegacy: (kind, key) => legacy.get(JSON.stringify([kind, key])) || null
  });
}

/** Inject getStorage: () => sessionStorage only from a future approved adapter.
 * Merely constructing/reading an empty store never writes. No alternate storage.
 */
function createInspirationStore({ registry, getStorage = () => null, now = Date.now, limit = MAX_ITEMS }) {
  if (!registry || typeof registry.resolve !== 'function' || !Number.isInteger(limit) || limit < 1 || limit > MAX_ITEMS) {
    throw new TypeError('Invalid store configuration');
  }
  let storage = null, state = null, revision = 0, notice = null;
  let mode = 'memory';
  const listeners = new Set(), plans = new WeakMap(), importPlans = new WeakMap();
  const loseStorage = () => { storage = null; mode = 'memory'; notice = 'storage_unavailable'; };
  const deleteOwnKey = () => {
    if (storage) try { storage.removeItem(STORAGE_KEY); } catch { loseStorage(); }
  };
  const clock = () => { try { return now(); } catch { return NaN; } };
  const status = item => {
    const current = registry.resolve(toIdentity(item));
    if (!current || current.state !== 'available') return 'unavailable';
    return current.content_version === item.content_version ? 'available' : 'changed';
  };
  const snapshot = () => freeze({
    catalog_namespace: registry.catalog_namespace, registry_version: registry.version,
    created_at: state?.created_at ?? null, expires_at: state?.expires_at ?? null,
    items: (state?.items || []).map(item => ({ ...item, status: status(item) })), mode, notice, revision
  });
  const publish = () => { const current = snapshot(); for (const listener of [...listeners]) { try { listener(current); } catch { /* observer cannot undo state */ } } };
  const invalidate = reason => { state = null; revision++; notice = reason; deleteOwnKey(); };
  const checkExpiry = () => {
    const at = clock();
    if (state && (!timeOK(at) || state.created_at > at || at >= state.expires_at)) {
      invalidate(timeOK(at) && at >= state.expires_at ? 'expired' : 'invalid_clock'); publish();
    }
    return at;
  };
  const validStored = record => {
    if (!own(record, envelopeFields) || record.schema_version !== 1 || record.catalog_namespace !== registry.catalog_namespace ||
        !token(record.registry_version) || !timeOK(record.created_at) || !timeOK(record.expires_at) ||
        record.expires_at - record.created_at !== TTL_MS || !Array.isArray(record.items) ||
        record.items.length < 1 || record.items.length > limit) return false;
    const unique = new Set();
    for (const item of record.items) {
      if (!own(item, itemFields) || !identity(toIdentity(item)) || !token(item.content_version)) return false;
      const current = registry.resolve(toIdentity(item));
      if (!current || item.line !== current.line || item.source !== current.source || unique.has(id(item))) return false;
      unique.add(id(item));
    }
    return true;
  };
  try {
    storage = getStorage();
    if (storage) {
      mode = 'session';
      const raw = storage.getItem(STORAGE_KEY);
      if (raw !== null) {
        try {
          if (typeof raw !== 'string' || bytes(raw) > MAX_RECORD_BYTES) throw new Error('Oversized record');
          const parsed = JSON.parse(raw);
          if (!validStored(parsed)) throw new Error('Invalid record');
          state = parsed;
        } catch { invalidate('invalid_record'); }
      }
    }
  } catch { loseStorage(); }
  checkExpiry();

  const commit = (items, importedCreatedAt = null) => {
    const before = revision;
    const at = checkExpiry();
    if (revision !== before) return freeze({ ok: false, reason: 'selection_changed' });
    if (!timeOK(at) || !timeOK(at + TTL_MS)) return freeze({ ok: false, reason: 'invalid_clock' });
    const createdAt = importedCreatedAt === null ? state?.created_at ?? at : Math.min(state?.created_at ?? at, importedCreatedAt);
    if (!timeOK(createdAt) || createdAt > at || at >= createdAt + TTL_MS) return freeze({ ok: false, reason: 'expired' });
    state = items.length ? {
      schema_version: 1, catalog_namespace: registry.catalog_namespace, registry_version: registry.version,
      created_at: createdAt, expires_at: createdAt + TTL_MS,
      items: items.map(reference)
    } : null;
    revision++; notice = mode === 'memory' ? 'storage_unavailable' : null;
    if (!state) deleteOwnKey();
    else if (storage) {
      try { storage.setItem(STORAGE_KEY, JSON.stringify(state)); }
      catch { deleteOwnKey(); loseStorage(); }
    }
    publish();
    return freeze({ ok: true, mode });
  };
  const get = () => { checkExpiry(); return snapshot(); };
  const add = value => {
    checkExpiry();
    const entry = registry.resolve(value);
    if (!entry) return freeze({ ok: false, reason: 'invalid_input' });
    if ((state?.items || []).some(item => id(item) === id(entry))) return freeze({ ok: true, reason: 'duplicate', mode });
    if (!entry.selectable || entry.state !== 'available') return freeze({ ok: false, reason: 'inactive_or_unavailable' });
    if ((state?.items.length || 0) >= limit) return freeze({ ok: false, reason: 'limit' });
    return commit([...(state?.items || []), entry]);
  };
  const remove = value => {
    checkExpiry();
    if (!identity(value)) return freeze({ ok: false, reason: 'invalid_input' });
    const previous = state?.items || [], next = previous.filter(item => id(item) !== id(value));
    return previous.length === next.length ? freeze({ ok: true, reason: 'unchanged', mode }) : commit(next);
  };
  const reviewForForm = identities => {
    const reviewedAt = checkExpiry();
    if (!timeOK(reviewedAt)) return freeze({ ok: false, reason: 'invalid_clock' });
    if (!Array.isArray(identities) || identities.length > limit) return freeze({ ok: false, reason: 'explicit_subset_required' });
    const selected = [], unique = new Set();
    for (const value of identities) {
      if (!identity(value) || unique.has(id(value))) return freeze({ ok: false, reason: 'invalid_input' });
      const item = (state?.items || []).find(item => id(item) === id(value));
      if (!item || status(item) !== 'available') return freeze({ ok: false, reason: 'unresolved_item' });
      unique.add(id(value)); selected.push(reference(item));
    }
    // Local expiry metadata stays outside selection; it is not a wire field.
    return freeze({ ok: true, reviewed_at: reviewedAt, expires_at: state?.expires_at ?? null,
      selection: { catalog_namespace: registry.catalog_namespace, registry_version: registry.version, items: selected } });
  };
  const readLegacy = (kind, raw) => {
    checkExpiry();
    if (!['prefall', 'bomber'].includes(kind) || typeof raw !== 'string' || bytes(raw) > MAX_LEGACY_BYTES) return freeze({ ok: false, reason: 'invalid_legacy' });
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return freeze({ ok: false, reason: 'invalid_legacy' }); }
    if (!Array.isArray(parsed) || parsed.length > 256) return freeze({ ok: false, reason: 'invalid_legacy' });
    const candidates = new Map((state?.items || []).map(item => [id(item), reference(item)]));
    let rejected = 0;
    for (const value of parsed) {
      const key = kind === 'prefall' && own(value, ['id', 'title']) && typeof value.title === 'string' ? value.id : kind === 'bomber' ? value : null;
      const entry = token(key) ? registry.resolveLegacy(kind, key) : null;
      if (!entry || entry.state !== 'available') { rejected++; continue; }
      if (!candidates.has(id(entry))) candidates.set(id(entry), reference(entry));
    }
    const result = freeze({ ok: true, reason: 'confirmation_required', candidates: [...candidates.values()], rejected, exceeds_limit: candidates.size > limit });
    plans.set(result, { candidates: [...candidates.values()], revision, kind });
    return result;
  };
  const confirmLegacy = (plan, chosen) => {
    checkExpiry();
    const saved = plans.get(plan);
    if (!saved || saved.revision !== revision) return freeze({ ok: false, reason: 'stale_review' });
    if (!Array.isArray(chosen) || chosen.length > limit) return freeze({ ok: false, reason: 'limit' });
    const selected = [], unique = new Set();
    for (const value of chosen) {
      if (!identity(value) || unique.has(id(value))) return freeze({ ok: false, reason: 'invalid_input' });
      const candidate = saved.candidates.find(item => id(item) === id(value));
      if (!candidate || status(candidate) !== 'available') return freeze({ ok: false, reason: 'unresolved_item' });
      unique.add(id(value)); selected.push(candidate);
    }
    const result = commit(selected);
    if (result.ok) plans.delete(plan);
    // Legacy writes belong to a future adapter with an explicit atomicity policy.
    return result;
  };
  const readImport = incoming => {
    const at = checkExpiry();
    if (!own(incoming, ['created_at', 'expires_at', 'items']) || !timeOK(at) ||
        !timeOK(incoming.created_at) || !timeOK(incoming.expires_at) || incoming.created_at > at || incoming.expires_at - incoming.created_at !== TTL_MS ||
        at >= incoming.expires_at || !Array.isArray(incoming.items) || incoming.items.length < 1 || incoming.items.length > limit) {
      return freeze({ ok: false, reason: 'invalid_import' });
    }
    const candidates = new Map((state?.items || []).map(item => [id(item), reference(item)]));
    const imported = new Set();
    for (const value of incoming.items) {
      if (!own(value, ['type', 'key', 'content_version']) || !identity(toIdentity(value)) || imported.has(id(value))) return freeze({ ok: false, reason: 'invalid_import' });
      const entry = registry.resolve(toIdentity(value));
      if (!entry || !entry.selectable || entry.state !== 'available' || entry.content_version !== value.content_version) return freeze({ ok: false, reason: 'unresolved_item' });
      imported.add(id(value));
      if (!candidates.has(id(value))) candidates.set(id(value), reference(entry));
    }
    const result = freeze({ ok: true, candidates: [...candidates.values()], exceeds_limit: candidates.size > limit });
    importPlans.set(result, { candidates: [...candidates.values()], imported, created_at: incoming.created_at, expires_at: incoming.expires_at, revision });
    return result;
  };
  const confirmImport = (plan, chosen) => {
    const at = checkExpiry(), saved = importPlans.get(plan);
    if (!saved || saved.revision !== revision || at >= saved.expires_at) return freeze({ ok: false, reason: 'stale_review' });
    if (!Array.isArray(chosen) || chosen.length > limit) return freeze({ ok: false, reason: 'limit' });
    const selected = [], unique = new Set();
    let includesImported = false;
    for (const value of chosen) {
      if (!identity(value) || unique.has(id(value))) return freeze({ ok: false, reason: 'invalid_input' });
      const candidate = saved.candidates.find(item => id(item) === id(value));
      if (!candidate || status(candidate) !== 'available') return freeze({ ok: false, reason: 'unresolved_item' });
      selected.push(candidate); unique.add(id(value));
      includesImported ||= saved.imported.has(id(value));
    }
    const result = commit(selected, includesImported ? saved.created_at : null);
    if (result.ok) importPlans.delete(plan);
    return result;
  };
  return Object.freeze({ get, add, remove, reviewForForm, readLegacy, confirmLegacy, readImport, confirmImport,
    clear: () => { checkExpiry(); return state ? commit([]) : freeze({ ok: true, reason: 'unchanged', mode }); },
    refresh: get, // A future approved focus/pageshow adapter calls this; no listeners installed here.
    subscribe: listener => {
      if (typeof listener !== 'function') throw new TypeError('Expected callback');
      listeners.add(listener); return () => listeners.delete(listener);
    }
  });
}

const TRANSFER_ORIGINS = Object.freeze(['https://consiglieri.ro', 'https://consiglierimen.com']);
const TRANSFER_MAX_CHARS = 1800;
const TRANSFER_WINDOW_MS = 10 * 60 * 1000;
const transferOwn = (value, fields) => value && Object.getPrototypeOf(value) === Object.prototype &&
  Reflect.ownKeys(value).length === fields.length && fields.every(key => Object.hasOwn(value, key));
const transferIdentity = value => ({ type: value.type, key: value.key });
const transferKey = value => JSON.stringify([value.type, value.key]);
const transferTime = value => Number.isSafeInteger(value) && value >= 0;
const transferEncode = value => btoa(JSON.stringify(value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const transferDecode = code => {
  if (typeof code !== 'string' || code.length > TRANSFER_MAX_CHARS || !/^CSI1\.[A-Za-z0-9_-]+$/.test(code)) throw new Error('invalid_code');
  const body = code.slice(5);
  const result = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - body.length % 4) % 4)));
  if (`CSI1.${transferEncode(result)}` !== code) throw new Error('invalid_code');
  return result;
};

function createManualTransfer({ store, registry, origin, now = Date.now }) {
  const target = TRANSFER_ORIGINS.find(value => value !== origin);
  const enabled = TRANSFER_ORIGINS.includes(origin);
  const reviewDeadlines = new WeakMap();
  return Object.freeze({
    enabled,
    target: enabled ? target : null,
    exportCode() {
      if (!enabled) return Object.freeze({ ok: false, reason: 'unsupported_origin' });
      const state = store.get(), at = now();
      if (!transferTime(at) || !state.items.length || !transferTime(state.created_at) || at >= state.expires_at) return Object.freeze({ ok: false, reason: 'empty_or_expired' });
      if (state.items.some(item => item.status !== 'available' || !registry.resolve(transferIdentity(item))?.selectable)) return Object.freeze({ ok: false, reason: 'unresolved_item' });
      const packet = { v: 1, n: registry.catalog_namespace, f: origin, t: target, i: at, c: state.created_at, e: state.expires_at,
        s: state.items.map(item => ({ type: item.type, key: item.key, content_version: item.content_version })) };
      const code = `CSI1.${transferEncode(packet)}`;
      return Object.freeze(code.length <= TRANSFER_MAX_CHARS ? { ok: true, code, expires_at: Math.min(at + TRANSFER_WINDOW_MS, state.expires_at), target } : { ok: false, reason: 'code_too_long' });
    },
    previewCode(code) {
      if (!enabled) return Object.freeze({ ok: false, reason: 'unsupported_origin' });
      try {
        const packet = transferDecode(code), at = now();
        if (!transferOwn(packet, ['v','n','f','t','i','c','e','s']) || packet.v !== 1 || packet.n !== registry.catalog_namespace ||
          packet.f !== target || packet.t !== origin || !transferTime(at) || !transferTime(packet.i) || packet.i > at + 60000 ||
          at >= packet.i + TRANSFER_WINDOW_MS || !transferTime(packet.c) || !transferTime(packet.e) || packet.c > packet.i || packet.e - packet.c !== 86400000 || at >= packet.e ||
          !Array.isArray(packet.s) || packet.s.length < 1 || packet.s.length > 8) throw new Error('invalid_code');
        const seen = new Set();
        for (const item of packet.s) {
          if (!transferOwn(item, ['type','key','content_version']) || seen.has(transferKey(item))) throw new Error('invalid_code');
          seen.add(transferKey(item));
        }
        const plan = store.readImport({ created_at: packet.c, expires_at: packet.e, items: packet.s });
        if (plan.ok) reviewDeadlines.set(plan, { deadline: Math.min(packet.i + TRANSFER_WINDOW_MS, packet.e), issued_at: packet.i });
        return plan;
      } catch { return Object.freeze({ ok: false, reason: 'invalid_code' }); }
    },
    confirm(plan, chosen) {
      const guard = reviewDeadlines.get(plan), at = now();
      if (!guard || !transferTime(at) || at >= guard.deadline || at + 60000 < guard.issued_at) return Object.freeze({ ok: false, reason: 'expired_code' });
      const result = store.confirmImport(plan, chosen);
      if (result.ok) reviewDeadlines.delete(plan);
      return result;
    }
  });
}

const MESSAGE_KEYS = Object.freeze(['appointment','title','open','close','empty','session','memory','preview','remove','clear','clear_question','confirm_clear','cancel','limit','saved','removed','expired','invalid','unavailable','changed','review','review_help','confirm','legacy','legacy_help','import_title','import_help','transfer','transfer_help','export','copy','copied','copy_manual','code','paste','preview_code','invalid_code','imported','choose_limit','cancelled','busy']);
const uiIdentity = item => ({ type: item.type, key: item.key });
const uiKey = item => JSON.stringify([item.type, item.key]);
const uiFreeze = value => Object.freeze(value);

function createInspirationUI({ window: win, document: doc, root, store, registry, transfer, texts, locale = 'ro', media = {}, getStorage = () => null, now = Date.now }) {
  if (!root || !MESSAGE_KEYS.every(key => typeof texts?.[key] === 'string' && texts[key].trim()) || typeof doc.createElement('dialog').showModal !== 'function') return null;
  const t = key => texts[key];
  let activeMode = 'browse', pending = null, returnFocus = null, bridge = null, renderBusy = false;
  let choices = new Set(), candidates = [], reviewRevision = null, clearPending = false, summaryMounts = new Set();
  const node = (tag, text, className) => { const element = doc.createElement(tag); if (text !== undefined) element.textContent = text; if (className) element.className = className; return element; };
  const button = (text, action, className = '') => { const element = node('button', text, `cs-inspiration__button ${className}`); element.type = 'button'; element.addEventListener('click', action); return element; };
  const launcher = button(t('open'), () => open(), 'cs-inspiration__launcher');
  launcher.setAttribute('aria-haspopup', 'dialog'); launcher.setAttribute('aria-controls', 'cs-inspiration-dialog');
  const announcer = node('p', '', 'cs-inspiration__sr'); announcer.setAttribute('role', 'status'); announcer.setAttribute('aria-live', 'polite'); announcer.setAttribute('aria-atomic', 'true');
  const dialog = node('dialog', undefined, 'cs-inspiration__dialog'); dialog.id = 'cs-inspiration-dialog'; dialog.setAttribute('aria-labelledby', 'cs-inspiration-heading');
  const panel = node('div', undefined, 'cs-inspiration__panel');
  const header = node('div', undefined, 'cs-inspiration__header');
  const heading = node('h2', t('title')); heading.id = 'cs-inspiration-heading';
  const closeButton = button(t('close'), () => close());
  header.append(heading, closeButton);
  const help = node('p', t('preview'), 'cs-inspiration__help');
  const storageNote = node('p', '', 'cs-inspiration__note');
  const status = node('p', '', 'cs-inspiration__status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
  const list = node('ul', undefined, 'cs-inspiration__list'); list.setAttribute('aria-label', t('title'));
  const actions = node('div', undefined, 'cs-inspiration__actions');
  const clearButton = button(t('clear'), () => { clearPending = true; renderActions(); });
  const clearBox = node('div', undefined, 'cs-inspiration__confirm'); clearBox.hidden = true;
  const clearText = node('p', t('clear_question'));
  const clearYes = button(t('confirm_clear'), () => { store.clear(); clearPending = false; render(); announce(t('removed')); closeButton.focus(); });
  const clearNo = button(t('cancel'), () => { clearPending = false; renderActions(); clearButton.focus(); });
  clearBox.append(clearText, clearYes, clearNo);
  const confirmButton = button(t('confirm'), () => confirmSelection(), 'cs-inspiration__primary');
  const cancelButton = button(t('cancel'), () => close());
  const appointmentLink=node('a',t('appointment'),'cs-inspiration__button cs-inspiration__primary');appointmentLink.href='/pages/programeaza';
  actions.append(appointmentLink, clearButton, clearBox, confirmButton, cancelButton);
  const transferBox = node('details', undefined, 'cs-inspiration__transfer');
  transferBox.append(node('summary', t('transfer')));
  const transferHelp = node('p', t('transfer_help'));
  const exportButton = button(t('export'), () => {
    const result = transfer.exportCode();
    if (!result.ok) { announce(t(result.reason === 'empty_or_expired' ? 'empty' : 'invalid_code')); return; }
    output.value = result.code; outputWrap.hidden = false; output.focus(); output.select();
  });
  const outputWrap = node('div'); outputWrap.hidden = true;
  const outputLabel = node('label', t('code')); outputLabel.htmlFor = 'cs-inspiration-export';
  const output = node('textarea'); output.id = 'cs-inspiration-export'; output.readOnly = true; output.rows = 3; output.spellcheck = false;
  const copyButton = button(t('copy'), async () => {
    if (!output.value) return;
    output.focus(); output.select();
    try { if (!win.navigator.clipboard?.writeText) throw new Error(); await win.navigator.clipboard.writeText(output.value); announce(t('copied')); }
    catch { announce(t('copy_manual')); }
  });
  outputWrap.append(outputLabel, output, copyButton);
  const inputLabel = node('label', t('paste')); inputLabel.htmlFor = 'cs-inspiration-import';
  const input = node('textarea'); input.id = 'cs-inspiration-import'; input.rows = 3; input.maxLength = 1800; input.spellcheck = false; input.autocomplete = 'off';
  const importButton = button(t('preview_code'), () => {
    const plan = transfer.previewCode(input.value.trim());
    if (!plan.ok) { announce(t('invalid_code')); return; }
    begin('import', plan.candidates, () => transfer.confirm(plan, selectedIdentities())).then(result => { if (result.ok) { input.value = ''; announce(t('imported')); } });
  });
  transferBox.append(transferHelp, exportButton, outputWrap, inputLabel, input, importButton);
  panel.append(header, help, storageNote, status, list, actions, transferBox); dialog.append(panel); root.append(launcher, announcer, dialog);
  function announce(message) { announcer.textContent = message; status.textContent = message; }
  function selectedIdentities() { return candidates.filter(item => choices.has(uiKey(item))).map(uiIdentity); }
  function renderActions() {
    const browsing = activeMode === 'browse', count = store.get().items.length;
    appointmentLink.hidden = !browsing || !count;
    clearButton.hidden = !browsing || !count || clearPending;
    clearBox.hidden = !browsing || !clearPending;
    confirmButton.hidden = browsing; cancelButton.hidden = browsing;
    confirmButton.disabled = choices.size > 8;
    transferBox.hidden = !browsing || !transfer.enabled;
    if (clearPending) clearYes.focus();
  }
  function render() {
    if (renderBusy) return;
    renderBusy = true;
    const state = store.get();
    const focusIdentity = doc.activeElement?.dataset?.csInspirationItem;
    const focusIndex = [...list.children].findIndex(element => element.contains(doc.activeElement));
    launcher.hidden = !!doc.querySelector('[data-fw26-campaign],[data-fw26-atelier]') && state.items.length === 0;
    launcher.textContent = `${t('open')} (${state.items.length})`;
    for (const mount of summaryMounts) { if(mount.element.closest('.cs-fw26-booking')) mount.element.hidden=state.items.length===0; mount.label.textContent = `${t('title')}: ${state.items.length}`; mount.names.textContent=state.items.map(item=>registry.resolve(uiIdentity(item))?.labels[locale]||item.key).join(' · '); }
    storageNote.textContent = t(state.mode === 'memory' ? 'memory' : 'session');
    if (state.notice === 'expired' || state.notice === 'invalid_clock') status.textContent = t('expired');
    heading.textContent = t(activeMode === 'legacy' ? 'legacy' : activeMode === 'import' ? 'import_title' : activeMode === 'review' ? 'review' : 'title');
    help.textContent = t(activeMode === 'legacy' ? 'legacy_help' : activeMode === 'import' ? 'import_help' : activeMode === 'review' ? 'review_help' : 'preview');
    const rows = activeMode === 'browse' ? state.items : candidates;
    const fragment = doc.createDocumentFragment();
    for (const item of rows) {
      const resolved = registry.resolve(uiIdentity(item));
      const itemStatus = state.items.find(current => uiKey(current) === uiKey(item))?.status || (resolved?.state === 'available' ? 'available' : 'unavailable');
      const label = resolved?.labels[locale] || item.key;
      const row = node('li', undefined, 'cs-inspiration__item');
      const mediaURL = media[uiKey(item)];
      if (typeof mediaURL === 'string' && /^(?:\/cdn\/[a-zA-Z0-9_./?=&%-]+|https:\/\/cdn\.shopify\.com\/[a-zA-Z0-9_./?=&%-]+)$/.test(mediaURL) && !mediaURL.includes('..')) {
        const thumbnail = node('img', undefined, 'cs-inspiration__thumbnail'); thumbnail.src = mediaURL; thumbnail.alt = ''; thumbnail.width = 64; thumbnail.height = 80; thumbnail.loading = 'lazy'; row.append(thumbnail);
      }
      const textBox = node('div', undefined, 'cs-inspiration__item-text');
      if (activeMode !== 'browse') {
        const labelBox = node('label', undefined, 'cs-inspiration__choice');
        const checkbox = node('input'); checkbox.type = 'checkbox'; checkbox.checked = choices.has(uiKey(item)); checkbox.disabled = itemStatus !== 'available'; checkbox.dataset.csInspirationItem = uiKey(item);
        checkbox.addEventListener('change', () => { if (checkbox.checked) choices.add(uiKey(item)); else choices.delete(uiKey(item)); renderActions(); status.textContent = choices.size > 8 ? t('choose_limit') : `${choices.size} / 8`; });
        labelBox.append(checkbox, node('span', label)); textBox.append(labelBox);
      } else textBox.append(node('span', label, 'cs-inspiration__name'));
      textBox.append(node('small', item.line.toUpperCase(), 'cs-inspiration__meta'));
      if (itemStatus !== 'available') textBox.append(node('p', t(itemStatus === 'changed' ? 'changed' : 'unavailable'), 'cs-inspiration__warning'));
      row.append(textBox);
      if (activeMode === 'browse') {
        const removeButton = button(t('remove'), () => { store.remove(uiIdentity(item)); announce(t('removed')); });
        removeButton.setAttribute('aria-label', `${t('remove')}: ${label}`); removeButton.dataset.csInspirationItem = uiKey(item); row.append(removeButton);
      }
      fragment.append(row);
    }
    if (!rows.length) fragment.append(node('li', t('empty'), 'cs-inspiration__empty'));
    list.replaceChildren(fragment); renderActions();
    if (focusIdentity) {
      const replacement = [...list.querySelectorAll('[data-cs-inspiration-item]')].find(element => element.dataset.csInspirationItem === focusIdentity);
      (replacement || list.children[Math.max(0, Math.min(focusIndex, list.children.length - 1))]?.querySelector('button,input') || closeButton).focus();
    }
    renderBusy = false;
  }
  function open(reason) {
    if (activeMode !== 'browse') return;
    if (!dialog.open) { returnFocus = doc.activeElement; render(); dialog.showModal(); closeButton.focus(); }
    if (reason === 'limit') announce(t('limit'));
    else if (['unavailable', 'inactive_or_unavailable', 'invalid_input'].includes(reason)) announce(t('unavailable'));
  }
  function finish(result) {
    const resolver = pending?.resolve; pending = null; activeMode = 'browse'; choices.clear(); candidates = []; reviewRevision = null;
    if (dialog.open) dialog.close(); clearPending = false; status.textContent = ''; render();
    const canRestoreFocus = element => element?.isConnected && element.getClientRects().length && !element.disabled && win.getComputedStyle(element).visibility === 'visible';
    const focusTarget = canRestoreFocus(returnFocus) ? returnFocus : [...doc.querySelectorAll('.cs-inspiration__form-summary button')].find(canRestoreFocus) || [...doc.querySelectorAll('main a[href], main button, header a[href]')].find(canRestoreFocus);
    if (focusTarget) focusTarget.focus(); returnFocus = null;
    resolver?.(result);
  }
  function close() { finish(uiFreeze({ ok: false, reason: 'cancelled' })); }
  function begin(mode, entries, commit) {
    if (pending) return Promise.resolve(uiFreeze({ ok: false, reason: 'busy' }));
    const state = store.get();
    candidates = entries; reviewRevision = state.revision;
    choices = new Set(entries.filter(item => {
      const current = state.items.find(value => uiKey(value) === uiKey(item));
      return (current ? current.status === 'available' : registry.resolve(uiIdentity(item))?.state === 'available');
    }).map(uiKey));
    activeMode = mode; clearPending = false; status.textContent = '';
    if (!dialog.open) returnFocus = doc.activeElement;
    const promise = new Promise(resolve => { pending = { resolve, commit }; });
    render(); if (!dialog.open) dialog.showModal(); closeButton.focus();
    return promise;
  }
  function confirmSelection() {
    if (!pending) return;
    if (store.get().revision !== reviewRevision) { announce(t('invalid')); finish(uiFreeze({ ok: false, reason: 'stale_review' })); return; }
    if (choices.size > 8) { announce(t('choose_limit')); return; }
    const result = pending.commit();
    if (!result.ok) { announce(t(result.reason === 'limit' ? 'choose_limit' : 'invalid')); return; }
    finish(result);
  }
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('keydown', event => {
    if (event.key !== 'Tab' || !dialog.open) return;
    const controls = [...dialog.querySelectorAll('button,input,textarea,summary,a,[tabindex]')].filter(element => {
      const collapsed = element.closest('details:not([open])');
      return !element.disabled && element.getAttribute('tabindex') !== '-1' && element.getClientRects().length && (!collapsed || collapsed.querySelector('summary')?.contains(element));
    });
    const first = controls[0], last = controls.at(-1);
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (doc.activeElement === last || doc.activeElement === dialog)) { event.preventDefault(); first.focus(); }
  });
  dialog.addEventListener('click', event => { if (event.target !== dialog) return; const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close(); });
  store.subscribe(() => { output.value = ''; outputWrap.hidden = true; render(); });
  win.addEventListener('focus', () => store.refresh()); win.addEventListener('pageshow', () => { store.refresh(); render(); });
  const api = {
    ready: true, locale,
    get: store.get, resolve: registry.resolve, resolveLegacy: registry.resolveLegacy,
    add(value) { const result = store.add(value); announce(t(result.ok ? 'saved' : result.reason === 'limit' ? 'limit' : 'invalid')); return result; },
    remove(value) { const result = store.remove(value); announce(t(result.ok ? 'removed' : 'invalid')); return result; },
    subscribe: store.subscribe, open,
    clear() { open(); clearPending = true; renderActions(); },
    readLegacy: store.readLegacy, confirmLegacy: store.confirmLegacy,
    requestLegacyReview(kind, raw, onConfirm) {
      const plan = store.readLegacy(kind, raw);
      if (!plan.ok) return Promise.resolve(plan);
      return begin('legacy', plan.candidates, () => store.confirmLegacy(plan, selectedIdentities())).then(result => { if (result.ok) onConfirm?.(result); return result; });
    },
    review() { const state = store.get(); return begin('review', state.items, () => store.reviewForForm(selectedIdentities())); },
    reviewForForm: store.reviewForForm,
    mountSummary(element) {
      if (!element || element.hasAttribute('data-cs-inspiration-summary-mounted')) return () => {};
      element.setAttribute('data-cs-inspiration-summary-mounted', '');
      if(element.closest('.cs-fw26-booking')) element.hidden=store.get().items.length===0;
      const label = node('span', `${t('title')}: ${store.get().items.length}`);
      const names=node('p');names.textContent=store.get().items.map(item=>registry.resolve(uiIdentity(item))?.labels[locale]||item.key).join(' · ');
      const edit = button(t('review'), () => open()); element.append(label, names, edit);
      const mount = { element, label, names }; summaryMounts.add(mount);
      return () => { summaryMounts.delete(mount); label.remove(); names.remove(); edit.remove(); element.removeAttribute('data-cs-inspiration-summary-mounted'); };
    },
    installBridge(factory) {
      if (!bridge && typeof factory === 'function') { bridge = factory(api, { getStorage, now }); win.dispatchEvent(new win.CustomEvent('cs:inspiration-ready')); }
      return bridge;
    },
    get legacy() { return bridge; }
  };
  render();
  return Object.freeze(api);
}

const PREFALL_KEY = 'consiglieri_pf26_private_edit_v2';
const PREFALL_OWNER_KEY = 'cs_inspiration_prefall_owner_v1';
const TTL = 86400000;
const read = (storage, key) => { try { return storage?.getItem(key) ?? null; } catch { return null; } };
const remove = (storage, key) => { try { storage?.removeItem(key); return true; } catch { return false; } };
const write = (storage, key, value) => { try { if (!storage) return false; storage.setItem(key, value); return true; } catch { return false; } };

function installLegacyBridge(api, { getStorage = () => null, now = Date.now } = {}) {
  if (!api || !['get', 'add', 'remove', 'subscribe', 'resolve', 'requestLegacyReview'].every(key => typeof api[key] === 'function')) throw new TypeError('Common selection owner required');
  let storage;
  try { storage = getStorage(); } catch { storage = null; }
  let owned = false;
  let receipt = null;
  let lastMirror = null;
  let syncing = false;
  const controllers = new Set();
  const initial = api.get();
  const namespace = initial.catalog_namespace;
  const rawReceipt = read(storage, PREFALL_OWNER_KEY);
  if (rawReceipt !== null) {
    try {
      const value = JSON.parse(rawReceipt);
      if (!value || Object.keys(value).sort().join(',') !== 'catalog_namespace,expires_at,schema_version' ||
          value.schema_version !== 1 || value.catalog_namespace !== namespace ||
          !Number.isSafeInteger(value.expires_at) || value.expires_at < 0 || value.expires_at > now() + TTL) throw new Error('Invalid mirror receipt');
      receipt = value;
      owned = true;
    } catch { remove(storage, PREFALL_OWNER_KEY); }
  }
  const project = snapshot => snapshot.items.flatMap(item => {
    const entry = api.resolve({ type: item.type, key: item.key });
    if (!entry || item.status !== 'available' || entry.type !== 'look' || entry.legacy?.kind !== 'prefall') return [];
    return [{ id: entry.legacy.id, title: entry.labels[api.locale === 'en' ? 'en' : 'ro'] }];
  });
  const makeReceipt = snapshot => ({ schema_version: 1, catalog_namespace: namespace, expires_at: snapshot.expires_at ?? now() + TTL });
  const sync = snapshot => {
    if (!owned || syncing) return;
    syncing = true;
    try {
      const selection = project(snapshot);
      const raw = JSON.stringify(selection);
      if (!selection.length || snapshot.mode !== 'session') {
        remove(storage, PREFALL_KEY);
        lastMirror = null;
        // Keep a receipt until native fallback has a chance to load: a failed mirror removal must never revive a saved set.
        if (!receipt) receipt = makeReceipt(snapshot);
        write(storage, PREFALL_OWNER_KEY, JSON.stringify(receipt));
      } else {
        receipt = makeReceipt(snapshot);
        // Do not create a durable mirror without its ownership/expiry receipt.
        if (write(storage, PREFALL_OWNER_KEY, JSON.stringify(receipt))) {
          if (raw !== lastMirror && write(storage, PREFALL_KEY, raw)) lastMirror = raw;
        } else { remove(storage, PREFALL_KEY); lastMirror = null; }
      }
      for (const controller of [...controllers]) controller(selection);
    } finally { syncing = false; }
  };
  if (owned) sync(initial);
  api.subscribe(sync);

  const attachPrefall = ({ raw = '[]', onChange = () => {} } = {}) => {
    let pendingRaw = raw;
    let locallyOwned = owned;
    try {
      const previous = JSON.parse(raw);
      if (Array.isArray(previous) && previous.length === 0) locallyOwned = true;
    } catch { /* Invalid legacy input stays a review candidate; no silent overwrite. */ }
    const notify = selection => {
      if (owned) locallyOwned = true;
      if (locallyOwned) onChange(selection.map(item => ({ ...item })));
    };
    controllers.add(notify);
    const establish = () => {
      owned = true;
      locallyOwned = true;
      receipt = makeReceipt(api.get());
    };
    if (locallyOwned && !owned && project(api.get()).length) { establish(); sync(api.get()); }
    return Object.freeze({
      isOwned: () => locallyOwned,
      getSelection: () => locallyOwned ? project(api.get()) : null,
      updatePending: value => { if (!locallyOwned && typeof value === 'string') pendingRaw = value; },
      review: async value => {
        if (typeof value === 'string') pendingRaw = value;
        const result = await api.requestLegacyReview('prefall', pendingRaw);
        if (result?.ok) { establish(); sync(api.get()); }
        return result;
      },
      add: id => {
        if (!locallyOwned) return { ok: false, reason: 'confirmation_required' };
        api.get();
        const previouslyOwned = owned;
        const result = api.add({ type: 'look', key: id });
        if (result?.ok && (!previouslyOwned || result.reason === 'duplicate')) { establish(); sync(api.get()); }
        return result;
      },
      remove: id => {
        if (!locallyOwned) return { ok: false, reason: 'confirmation_required' };
        const result = api.remove({ type: 'look', key: id });
        if (result?.ok && owned && result.reason === 'unchanged') sync(api.get());
        return result;
      },
      disconnect: () => controllers.delete(notify)
    });
  };
  return Object.freeze({ attachPrefall });
}

function bootInspiration(win = window, doc = document) {
  if (win.CSInspiration?.ready) return win.CSInspiration;
  const config = win.CSInspirationConfig;
  const root = doc.querySelector('[data-cs-inspiration-root]');
  const copyElement = root?.querySelector('[data-cs-inspiration-copy]');
  if (config?.enabled !== true || !root || !copyElement || !config.registry) return null;
  try {
    const copy = JSON.parse(copyElement.textContent), registry = createRegistry(config.registry);
    const getStorage = () => win.sessionStorage;
    const store = createInspirationStore({ registry, getStorage });
    const transfer = createManualTransfer({ store, registry, origin: win.location.origin });
    const api = createInspirationUI({ window: win, document: doc, root, store, registry, transfer, texts: copy.texts, locale: copy.locale === 'en' ? 'en' : 'ro', media: config.media || {}, getStorage });
    if (!api) return null;
    win.CSInspiration = api;
    if (typeof installLegacyBridge === 'function') api.installBridge(installLegacyBridge);
    win.dispatchEvent(new win.CustomEvent('cs:inspiration-ready'));
    return api;
  } catch { return null; }
}
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bootInspiration(), { once: true });
  else bootInspiration();
}

})();

